/**
 * UsageTrendCard — shared trend visualiser for the super-admin console.
 *
 * Used by:
 *   - superadmin-dashboard.tsx (cross-tenant trend, hits /v1/superadmin/trends)
 *   - superadmin-tenant-detail.tsx (per-tenant trend, hits /v1/superadmin/societies/:id/usage)
 *
 * Design choices:
 *   - Pure SVG bar chart, ~40 lines of render code, no chart deps.
 *   - Metric selector pills (single-select), one per series field.
 *   - Window selector dropdown (30 / 90 days). Bucket is server-defaulted
 *     (`day` for 30d, `week` for 90d) so the bar count stays readable.
 *   - Hover `<title>` element surfaces the per-bar value + bucket date so
 *     the visualisation is keyboard-accessible (focus a bar → tooltip).
 *   - Below the chart: Total + Avg/bucket + Peak (value + date).
 *
 * The cross-tenant variant exposes an extra "Active tenants" metric tab
 * driven by the `active_tenants` field on each bucket — only present in
 * the /trends response. Per-tenant callers leave `includeActiveTenants`
 * off and that tab is hidden.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';

interface TrendBucket {
  bucket_start: string;
  visitors: number;
  notices: number;
  complaints: number;
  bills: number;
  payments: number;
  documents: number;
  active_tenants?: number;
}

interface TrendTotals {
  visitors: number;
  notices: number;
  complaints: number;
  bills: number;
  payments: number;
  documents: number;
}

interface TrendResponse {
  from: string;
  to: string;
  bucket: 'day' | 'week';
  series: TrendBucket[];
  totals: TrendTotals;
  /** Only present on cross-tenant /trends. */
  peak_active_tenants?: number;
  /** Only present on /societies/:id/usage. */
  society_id?: string;
}

type MetricKey = keyof Pick<
  TrendBucket,
  'visitors' | 'notices' | 'bills' | 'payments' | 'complaints' | 'documents' | 'active_tenants'
>;

const METRIC_LABEL: Record<MetricKey, string> = {
  visitors: 'Visitors',
  notices: 'Notices',
  bills: 'Bills',
  payments: 'Payments',
  complaints: 'Complaints',
  documents: 'Documents',
  active_tenants: 'Active tenants'
};

const METRIC_ORDER: MetricKey[] = ['visitors', 'notices', 'bills', 'payments', 'complaints', 'documents'];

const WINDOW_OPTIONS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' }
] as const;
type WindowDays = (typeof WINDOW_OPTIONS)[number]['value'];

export interface UsageTrendCardProps {
  /** Card title — varies by where it's mounted. */
  title: string;
  /**
   * The endpoint to hit. The card appends `?bucket=…&from=…&to=…` itself —
   * pass just the base path, e.g. "/superadmin/trends" or
   * "/superadmin/societies/<uuid>/usage".
   */
  endpoint: string;
  /** Cross-tenant variant turns on the "Active tenants" tab. */
  includeActiveTenants?: boolean;
  /** TanStack Query key segment so per-tenant + cross-tenant don't collide. */
  queryKey: ReadonlyArray<unknown>;
}

export function UsageTrendCard({
  title,
  endpoint,
  includeActiveTenants = false,
  queryKey
}: UsageTrendCardProps): JSX.Element {
  const [metric, setMetric] = useState<MetricKey>('visitors');
  const [windowDays, setWindowDays] = useState<WindowDays>(30);

  // Build from/to range. We send midnight-UTC ISO to keep buckets aligned
  // with the server-side date_trunc — even across DST the absolute moment
  // is what counts.
  const { from, to } = useMemo(() => {
    const now = new Date();
    const toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    const fromDate = new Date(toDate.getTime() - windowDays * 24 * 60 * 60 * 1000);
    return { from: fromDate.toISOString(), to: toDate.toISOString() };
  }, [windowDays]);

  // Let the server pick the natural bucket (day for 30d, week for 90d) by
  // omitting `bucket` from the query string.
  const trendQuery = useQuery<TrendResponse>({
    queryKey: [...queryKey, { from, to }],
    queryFn: () => {
      const qs = new URLSearchParams({ from, to });
      return get<TrendResponse>(`${endpoint}?${qs.toString()}`);
    }
  });

  const tabs = useMemo<MetricKey[]>(() => {
    return includeActiveTenants ? [...METRIC_ORDER, 'active_tenants'] : METRIC_ORDER;
  }, [includeActiveTenants]);

  const series = trendQuery.data?.series ?? [];

  // Build the y-values for the active metric. Default 0 keeps the SVG
  // mathematically sane when a bucket lacks the chosen field (server pads
  // those to 0, but TS doesn't know).
  const values = series.map((row) => (row[metric] ?? 0) as number);
  const max = values.length > 0 ? Math.max(...values, 1) : 1;
  const total = values.reduce((a, b) => a + b, 0);
  const avg = values.length > 0 ? total / values.length : 0;
  const peakIdx = values.length > 0 ? values.indexOf(Math.max(...values)) : -1;
  const peakValue = peakIdx >= 0 ? values[peakIdx]! : 0;
  const peakDate = peakIdx >= 0 ? series[peakIdx]!.bucket_start : null;

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <div className="flex flex-wrap items-center justify-between gap-vt-2">
          <div className="flex items-center gap-vt-2">
            <Activity className="h-4 w-4 text-vt-text-muted" />
            <h2 className="text-vt-14 font-semibold text-vt-text">{title}</h2>
            <span className="text-vt-12 text-vt-text-soft">
              {trendQuery.data
                ? `${series.length} ${trendQuery.data.bucket === 'day' ? 'days' : 'weeks'}`
                : ''}
            </span>
          </div>
          <Select
            aria-label="Trend window"
            value={String(windowDays)}
            onChange={(e) => setWindowDays(Number.parseInt(e.target.value, 10) as WindowDays)}
            className="md:w-44"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Metric tab strip — pills similar to the Status filter on the
            dashboard for visual consistency. */}
        <div className="flex flex-wrap gap-vt-1" role="tablist" aria-label="Trend metric">
          {tabs.map((m) => {
            const active = metric === m;
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMetric(m)}
                className={
                  'h-8 rounded-vt-pill border px-3 text-vt-13 transition-colors duration-vt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400 ' +
                  (active
                    ? 'border-vt-primary bg-vt-primary text-white shadow-vt-1'
                    : 'border-vt-border bg-vt-surface text-vt-text-muted hover:border-vt-border-strong hover:text-vt-text')
                }
              >
                {METRIC_LABEL[m]}
              </button>
            );
          })}
        </div>

        {trendQuery.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : trendQuery.isError ? (
          <p className="text-vt-13 text-vt-alert" role="alert">
            {getErrorMessage(trendQuery.error)}
          </p>
        ) : series.length === 0 ? (
          <p className="py-vt-4 text-center text-vt-13 text-vt-text-muted">
            No activity in the selected window.
          </p>
        ) : (
          <TrendBars
            values={values}
            max={max}
            labels={series.map((r) => r.bucket_start)}
            metricLabel={METRIC_LABEL[metric]}
          />
        )}

        {/* Stats footer — Total / Avg / Peak. */}
        <div className="grid grid-cols-3 gap-vt-3 border-t border-vt-border pt-vt-3 text-vt-13">
          <div>
            <div className="text-vt-12 text-vt-text-muted">Total</div>
            <div className="font-mono text-vt-16 font-semibold text-vt-text">
              {total.toLocaleString('en-IN')}
            </div>
          </div>
          <div>
            <div className="text-vt-12 text-vt-text-muted">
              Avg/{trendQuery.data?.bucket === 'week' ? 'week' : 'day'}
            </div>
            <div className="font-mono text-vt-16 font-semibold text-vt-text">
              {avg.toFixed(1)}
            </div>
          </div>
          <div>
            <div className="text-vt-12 text-vt-text-muted">Peak</div>
            <div className="font-mono text-vt-16 font-semibold text-vt-text">
              {peakValue.toLocaleString('en-IN')}
              {peakDate ? (
                <span className="ml-1 text-vt-12 font-normal text-vt-text-soft">on {peakDate}</span>
              ) : null}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TrendBars — minimal pure-SVG bar chart.
//
// Layout: 600x160 viewBox, scales to container width. Bars are evenly
// distributed with a small inter-bar gap. Each bar carries a <title>
// child so hover (mouse) and focus (keyboard / VoiceOver) surface the
// human-readable value.
// ──────────────────────────────────────────────────────────────────────

function TrendBars({
  values,
  max,
  labels,
  metricLabel
}: {
  values: number[];
  max: number;
  labels: string[];
  metricLabel: string;
}): JSX.Element {
  const W = 600;
  const H = 160;
  const PAD_TOP = 8;
  const PAD_BOTTOM = 22;
  const PAD_LEFT = 4;
  const PAD_RIGHT = 4;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOTTOM;
  const n = values.length;
  const barSlot = chartW / Math.max(n, 1);
  const barW = Math.max(1, barSlot - 2);

  // Pick a handful of x-axis tick indices (~6 evenly distributed) so we
  // don't render 30+ overlapping date labels.
  const tickIndices = useMemo(() => {
    if (n === 0) return [] as number[];
    const target = Math.min(6, n);
    if (target === 1) return [0];
    const step = (n - 1) / (target - 1);
    const ticks = new Set<number>();
    for (let i = 0; i < target; i += 1) ticks.add(Math.round(i * step));
    return Array.from(ticks).sort((a, b) => a - b);
  }, [n]);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`${metricLabel} over ${n} buckets`}
      className="h-40 w-full"
    >
      {/* Baseline */}
      <line
        x1={PAD_LEFT}
        y1={H - PAD_BOTTOM}
        x2={W - PAD_RIGHT}
        y2={H - PAD_BOTTOM}
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth="1"
      />
      {/* Bars */}
      {values.map((v, i) => {
        const h = (v / max) * chartH;
        const x = PAD_LEFT + i * barSlot + (barSlot - barW) / 2;
        const y = H - PAD_BOTTOM - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(h, v === 0 ? 0 : 1)}
            rx={barW > 4 ? 2 : 0}
            className="fill-vt-primary"
            tabIndex={0}
            focusable="true"
          >
            <title>{`${labels[i]} — ${v.toLocaleString('en-IN')} ${metricLabel.toLowerCase()}`}</title>
          </rect>
        );
      })}
      {/* X-axis ticks */}
      {tickIndices.map((i) => {
        const x = PAD_LEFT + i * barSlot + barSlot / 2;
        const label = labels[i] ?? '';
        // "YYYY-MM-DD" → "MM-DD" to keep the tick compact
        const short = label.length >= 10 ? label.slice(5) : label;
        return (
          <text
            key={`tick-${i}`}
            x={x}
            y={H - 6}
            textAnchor="middle"
            className="fill-current text-vt-text-soft"
            style={{ fontSize: '10px' }}
          >
            {short}
          </text>
        );
      })}
    </svg>
  );
}
